import React from 'react';
import PropTypes from 'prop-types';
import SearchGroupInput from '../input/SearchGroupInput.component';
import {MY_TASKS_DATES} from "utils/constants";
import _ from "lodash";
import { DropdownSelect } from "components/common/dropdown/DropdownSelect.component";
import { DropdownSelectTypes } from "types/gaiaComponents/dropdownButton.types";
import { Checkbox } from "components/common/checkbox.component";
import { SegmentedControl } from "components/common/SegmentedControl.component";
import { connect } from 'react-redux';
import * as reduxSelectors from '../store/application.reducers';
import { toggleShowLastChangedBy } from './myTasks.action';
import { useTranslation } from 'components/translations/useTranslation';
import ColumnPicker from './ColumnPicker.component';
import { GROUP_BY } from './tasksGrouping.function';

const GROUP_OPTIONS = [
    {key: GROUP_BY.URGENCY, labelKey: "myTasks.overview.groupUrgency", dataCy: "myTasks.groupBy.urgency"},
    {key: GROUP_BY.COMPANY, labelKey: "myTasks.overview.groupCompany", dataCy: "myTasks.groupBy.company"},
    {key: GROUP_BY.FROM, labelKey: "myTasks.overview.groupFrom", dataCy: "myTasks.groupBy.from"},
    {key: GROUP_BY.DOCUMENT_TYPE, labelKey: "myTasks.overview.groupDocumentType", dataCy: "myTasks.groupBy.documentType"}
];

const SearchView = (props) => {
    const { translate } = useTranslation();

    let sort = function (value) {
        props.changeDisplayedDate(value);
    };

    const sortingDate = _.cloneDeep(MY_TASKS_DATES);

    let dateToPick = Object.keys(sortingDate).map((key) => ({
        key,
        text: translate("myTasks.selectDate." + MY_TASKS_DATES[key]),
        onClick: () => sort(MY_TASKS_DATES[key]),
        selected: props.displayedDate === MY_TASKS_DATES[key]
    }));

    let showLastChangedByCheckbox = (
        <div className="d-flex align-items-center text-nowrap">
            <Checkbox
                label={translate("myTasks.lastChangedBy")}
                isChecked={props.showLastChangedBy}
                onChange={() => props.toggleShowLastChangedBy(!props.showLastChangedBy)}
                dataCy="myTasks.lastChangedBy"
            />
        </div>
    );

    // the overview trades the all/current segmented control for a single "only this company" toggle
    let currentCompanyCheckbox = (
        <div className="d-flex align-items-center text-nowrap">
            <Checkbox
                label={translate("myTasks.currentCompany")}
                isChecked={props.onlyCurrentCompany}
                onChange={() => props.updateCompanyFilterCallback(!props.onlyCurrentCompany)}
                dataCy="myTasks.currentCompany"
            />
        </div>
    );

    // overview-only: the grid's grouping and column controls share this filter line
    let groupByControl = (
        <div className="tasks-filter__group tasks-filter__group--lead">
            <span className="tasks-filter__label">{translate("myTasks.overview.groupBy")}</span>
            <SegmentedControl
                ariaLabel={translate("myTasks.overview.groupBy")}
                options={GROUP_OPTIONS.map(option => ({
                    key: option.key,
                    label: translate(option.labelKey),
                    isSelected: props.groupBy === option.key,
                    onClick: () => props.onGroupByChange(option.key),
                    dataCy: option.dataCy
                }))}
            />
        </div>
    );

    let columnPicker = (
        <ColumnPicker
            columns={props.columns || []}
            onToggle={props.onColumnToggle}
            onReset={props.onColumnsReset}
            isCustomised={props.columnsCustomised}
            translate={translate}
        />
    );

    let chooseDateToDisplay = (
        <div className="d-flex align-items-center gap-8">
            <div className="text-nowrap">
                {translate("myTasks.selectDate.date")}
            </div>
            <div>
                <DropdownSelect
                    id="SelectMyTasksDate"
                    type={DropdownSelectTypes.VALUE}
                    value={translate("myTasks.selectDate." + props.displayedDate)}
                    items={dateToPick}
                    isEmpty={false}
                />
            </div>
        </div>
    );

    return (
        <section className={"container col-md-12 px-0" + (props.overviewEnabled ? " mb-12" : "")}>
            <div className="row margin-bottom mx-0">
                <aside className="search-group col-md-2 px-0" role="search">
                    <SearchGroupInput value={props.searchValue}
                                      onInputChange={(e) => {
                                          props.updateSearchCallback(e.target.value.toLowerCase())
                                      }}
                                      onClearInput={() => {
                                          props.updateSearchCallback()
                                      }}
                                      translate={translate}/>
                </aside>
                <aside
                    className={"col-md-10 d-flex flex-wrap justify-content-end align-items-center gap-16 "
                        + (props.overviewEnabled ? "pe-0" : "px-0")}
                    aria-label={translate("myTasks.filterMyTasks")}>
                    {/* the overview grid owns its columns, so the column-level filters are hidden there */}
                    {!props.overviewEnabled && showLastChangedByCheckbox}
                    {!props.overviewEnabled && chooseDateToDisplay}
                    {props.overviewEnabled && groupByControl}
                    {!props.oneCompanyOnlyUser && (props.overviewEnabled
                            ? currentCompanyCheckbox
                            : <SegmentedControl
                                ariaLabel={translate("myTasks.filterMyTasks")}
                                options={[
                                    {
                                        key: "allCompanies",
                                        label: translate("myTasks.allCompanies"),
                                        isSelected: !props.onlyCurrentCompany,
                                        onClick: () => props.updateCompanyFilterCallback(false),
                                        dataCy: "myTasks.allCompanies"
                                    },
                                    {
                                        key: "currentCompany",
                                        label: translate("myTasks.currentCompany"),
                                        isSelected: props.onlyCurrentCompany,
                                        onClick: () => props.updateCompanyFilterCallback(true),
                                        dataCy: "myTasks.currentCompany"
                                    }
                                ]}
                            />
                    )}
                    {props.overviewEnabled && columnPicker}
                </aside>
            </div>
        </section>
    );

}

const mapStateToProps = (state) => ({
    showLastChangedBy: reduxSelectors.getShowLastChangedBy(state),
});

const mapDispatchToProps = {
    toggleShowLastChangedBy,
};

export default connect(mapStateToProps, mapDispatchToProps)(SearchView);

SearchView.propTypes = {
    updateSearchCallback: PropTypes.func,
    updateCompanyFilterCallback: PropTypes.func,
    searchValue: PropTypes.string,
    onlyCurrentCompany: PropTypes.bool,
    oneCompanyOnlyUser: PropTypes.bool,
    changeDisplayedDate: PropTypes.func,
    displayedDate: PropTypes.string,
    // the overview (beta) list keeps its grouping and column controls on this same line
    overviewEnabled: PropTypes.bool,
    groupBy: PropTypes.string,
    onGroupByChange: PropTypes.func,
    columns: PropTypes.array,
    onColumnToggle: PropTypes.func,
    onColumnsReset: PropTypes.func,
    columnsCustomised: PropTypes.bool
};
